import React from 'react';
import { SectionCard } from '../common/SectionCard';
import { Button } from '../ui/Button';
import { formatAed } from '../../utils/format';
import type { ApplicationRecord, Track } from '../../types/domain';
type Measure = 'records' | 'support';
const TRACK_ORDER: Track[] = ['mega', 'major', 'international'];
const TRACK_LABEL: Record<Track, string> = {
  mega: 'Mega events',
  major: 'Major events',
  international: 'International events'
};
const TRACK_COLOUR: Record<Track, string> = {
  mega: 'var(--chart-1)',
  major: 'var(--chart-3)',
  international: 'var(--chart-6)'
};
const WIDTH = 860;
const HEIGHT = 400;
const NODE_WIDTH = 12;
const NODE_GAP = 12;
const LEFT_X = 190;
const RIGHT_X = 640;
interface Link {
  emirate: string;
  track: Track;
  value: number;
}
interface Ribbon extends Link {
  id: string;
  y0: number;
  y1: number;
  thickness: number;
}
interface NodeBox {
  key: string;
  label: string;
  value: number;
  y: number;
  height: number;
}
export function EmirateTrackFlow({
  records
}: {
  records: ApplicationRecord[];
}) {
  const [measure, setMeasure] = React.useState<Measure>('records');
  const [hovered, setHovered] = React.useState<Ribbon | null>(null);
  const [pointer, setPointer] = React.useState({
    x: 0,
    y: 0
  });
  const value = (record: ApplicationRecord) => measure === 'records' ? 1 : record.requestedSupportAed;
  const formatValue = (amount: number) => measure === 'records' ? `${amount} record${amount === 1 ? '' : 's'}` : formatAed(amount);
  const links: Link[] = React.useMemo(() => {
    const map = new Map<string, Link>();
    records.forEach(record => {
      const key = `${record.emirate}__${record.track}`;
      const existing = map.get(key);
      if (existing) existing.value += value(record);else map.set(key, {
        emirate: record.emirate,
        track: record.track,
        value: value(record)
      });
    });
    return Array.from(map.values());
  }, [records, measure]);
  const total = links.reduce((sum, link) => sum + link.value, 0);
  const emirates = React.useMemo(() => {
    const totals = new Map<string, number>();
    links.forEach(link => totals.set(link.emirate, (totals.get(link.emirate) ?? 0) + link.value));
    return Array.from(totals.entries()).sort((a, b) => b[1] - a[1]).map(([emirate, emirateTotal]) => ({
      emirate,
      total: emirateTotal
    }));
  }, [links]);
  const tracks = React.useMemo(() => TRACK_ORDER.map(track => ({
    track,
    total: links.filter(link => link.track === track).reduce((sum, link) => sum + link.value, 0)
  })).filter(entry => entry.total > 0), [links]);
  const leftScale = (HEIGHT - NODE_GAP * Math.max(emirates.length - 1, 0)) / Math.max(total, 1);
  const rightScale = (HEIGHT - NODE_GAP * Math.max(tracks.length - 1, 0)) / Math.max(total, 1);
  const leftNodes: NodeBox[] = [];
  let leftCursor = 0;
  emirates.forEach(entry => {
    const height = entry.total * leftScale;
    leftNodes.push({
      key: entry.emirate,
      label: entry.emirate,
      value: entry.total,
      y: leftCursor,
      height
    });
    leftCursor += height + NODE_GAP;
  });
  const rightNodes: NodeBox[] = [];
  let rightCursor = 0;
  tracks.forEach(entry => {
    const height = entry.total * rightScale;
    rightNodes.push({
      key: entry.track,
      label: TRACK_LABEL[entry.track],
      value: entry.total,
      y: rightCursor,
      height
    });
    rightCursor += height + NODE_GAP;
  });
  const ribbons: Ribbon[] = React.useMemo(() => {
    const leftOffsets = new Map<string, number>();
    const rightOffsets = new Map<string, number>();
    const result: Ribbon[] = [];
    emirates.forEach(entry => {
      const node = leftNodes.find(item => item.key === entry.emirate);
      if (!node) return;
      TRACK_ORDER.forEach(track => {
        const link = links.find(item => item.emirate === entry.emirate && item.track === track);
        if (!link) return;
        const rightNode = rightNodes.find(item => item.key === track);
        if (!rightNode) return;
        const leftUsed = leftOffsets.get(entry.emirate) ?? 0;
        const rightUsed = rightOffsets.get(track) ?? 0;
        result.push({
          ...link,
          id: `${entry.emirate}__${track}`,
          y0: node.y + leftUsed + link.value * leftScale / 2,
          y1: rightNode.y + rightUsed + link.value * rightScale / 2,
          thickness: Math.max(link.value * Math.min(leftScale, rightScale), 1.5)
        });
        leftOffsets.set(entry.emirate, leftUsed + link.value * leftScale);
        rightOffsets.set(track, rightUsed + link.value * rightScale);
      });
    });
    return result;
  }, [emirates, leftNodes, rightNodes, links, leftScale, rightScale]);
  const containerRef = React.useRef<HTMLDivElement>(null);
  function handleMove(event: React.MouseEvent) {
    const bounds = containerRef.current?.getBoundingClientRect();
    if (!bounds) return;
    setPointer({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });
  }
  const dominant = [...ribbons].sort((a, b) => b.value - a.value)[0];
  return <SectionCard title="Emirate to track flow" description="How each emirate's applications distribute across the Mega, Major and International tracks" actions={<div className="flex items-center gap-1" data-id="element-378" data-mp="2s2k1246dnre9">
          <Button size="xs" variant={measure === 'records' ? 'default' : 'outline'} onClick={() => setMeasure('records')} data-id="element-379" data-mp="2s4xwie4yz5mc">
            Records
          </Button>
          <Button size="xs" variant={measure === 'support' ? 'default' : 'outline'} onClick={() => setMeasure('support')} data-id="element-380" data-mp="31z6jynf588dn">
            Support (AED)
          </Button>
        </div>} data-id="element-377" data-mp="2j3gir2f1bg0t">
      <div ref={containerRef} className="relative" onMouseMove={handleMove} data-id="element-381" data-mp="3ppskzuqqcse5">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="h-[400px] w-full" role="img" aria-label="Sankey diagram of applications flowing from each emirate to the Mega, Major and International tracks" data-id="element-382" data-mp="3krr0r6hkin13">
          {ribbons.map(ribbon => {
          const x0 = LEFT_X + NODE_WIDTH;
          const x1 = RIGHT_X;
          const mid = (x0 + x1) / 2;
          const active = hovered?.id === ribbon.id;
          return <path key={ribbon.id} d={`M ${x0} ${ribbon.y0} C ${mid} ${ribbon.y0}, ${mid} ${ribbon.y1}, ${x1} ${ribbon.y1}`} stroke={TRACK_COLOUR[ribbon.track]} strokeWidth={ribbon.thickness} strokeOpacity={hovered ? active ? 0.85 : 0.14 : 0.42} fill="none" onMouseEnter={() => setHovered(ribbon)} onMouseLeave={() => setHovered(null)} data-id="element-383" data-mp="2w7c4enh84ex9" />;
        })}

          {leftNodes.map(node => <g key={node.key} data-id="element-384" data-mp="1krdktpof6ahs">
              <rect x={LEFT_X} y={node.y} width={NODE_WIDTH} height={Math.max(node.height, 2)} fill="var(--chart-2)" rx={1.5} data-id="element-385" data-mp="1kqklh9rsin9o" />
              <text x={LEFT_X - 10} y={node.y + node.height / 2} textAnchor="end" dominantBaseline="middle" fontSize={12} fill="var(--foreground)" data-id="element-386" data-mp="13gqhe46qe46z">
                {node.label}
              </text>
              <text x={LEFT_X - 10} y={node.y + node.height / 2 + 14} textAnchor="end" dominantBaseline="middle" fontSize={10} fill="var(--muted-foreground)" data-id="element-387" data-mp="1r62sj9lmcmbx">
                {formatValue(node.value)}
              </text>
            </g>)}

          {rightNodes.map(node => <g key={node.key} data-id="element-388" data-mp="6q7q4qt3wd9b">
              <rect x={RIGHT_X} y={node.y} width={NODE_WIDTH} height={Math.max(node.height, 2)} fill={TRACK_COLOUR[node.key as Track]} rx={1.5} data-id="element-389" data-mp="6p65bumagcee" />
              <text x={RIGHT_X + NODE_WIDTH + 10} y={node.y + node.height / 2} dominantBaseline="middle" fontSize={12} fill="var(--foreground)" data-id="element-390" data-mp="1wv2pwwkpon8e">
                {node.label}
              </text>
              <text x={RIGHT_X + NODE_WIDTH + 10} y={node.y + node.height / 2 + 14} dominantBaseline="middle" fontSize={10} fill="var(--muted-foreground)" data-id="element-391" data-mp="2tkttdlazdkqc">
                {formatValue(node.value)} · {Math.round(node.value / total * 100)}%
              </text>
            </g>)}
        </svg>

        {hovered && <div className="pointer-events-none absolute z-10 rounded-sm border border-border bg-card px-2.5 py-1.5 shadow-sm" style={{
        left: Math.min(pointer.x + 12, (containerRef.current?.clientWidth ?? 0) - 180),
        top: pointer.y + 12
      }} role="status" data-id="element-392" data-mp="3d9awhhp36lye">
            <p className="text-[12px] font-medium" data-id="element-393" data-mp="7i8aascj7jt">
              {hovered.emirate} → {TRACK_LABEL[hovered.track]}
            </p>
            <p className="font-mono text-[11px] text-muted-foreground" data-id="element-394" data-mp="33p0igovlcqne">
              {formatValue(hovered.value)} · {Math.round(hovered.value / total * 100)}% of
              portfolio
            </p>
          </div>}
      </div>

      {dominant && <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground" data-id="element-395" data-mp="3dohjuu3t8idk">
          Largest flow: {dominant.emirate} → {TRACK_LABEL[dominant.track]} at{' '}
          {formatValue(dominant.value)} ({Math.round(dominant.value / total * 100)}% of the
          portfolio). Hover a ribbon to isolate a single emirate-to-track flow.
        </p>}
    </SectionCard>;
}